/** Task-agnostic client for the inference platform's SSE endpoints.
 *
 *  Every task posts { image_b64, prompt? } and receives a stream of
 *  warming → running → complete (or error) events. The worker owns the
 *  prompts; the endpoint path selects the task.
 *
 *      for await (const event of streamInference({ imageB64, endpoint })) { … }
 */

/** Inactivity budget, not a total budget: the timer is rearmed every time
 *  bytes arrive. A cold GPU takes 60–100 s to boot but emits `warming` events
 *  the whole way, so this only fires when the stream has genuinely stalled. */
const DEFAULT_IDLE_TIMEOUT_MS = 180_000;

/**
 * @param {object} opts
 * @param {string} opts.imageB64   base64 image bytes, no data: prefix
 * @param {string} opts.endpoint   task endpoint, e.g. /api/inference/step1
 * @param {string} [opts.prompt]   overrides the worker's trained prompt
 * @param {string} [opts.token]    Supabase access token; omit for cookie auth
 * @param {AbortSignal} [opts.signal]
 * @param {number} [opts.idleTimeoutMs]  ms without bytes before giving up; 0 disables
 * @returns {AsyncGenerator<InferenceEvent>}
 */
export async function* streamInference({
  imageB64,
  endpoint,
  prompt,
  token,
  signal,
  idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
}) {
  if (!imageB64 || typeof imageB64 !== "string") throw new Error("imageB64 is required");
  if (!endpoint) throw new Error("endpoint is required");

  const headers = { "content-type": "application/json" };
  if (token) headers.authorization = `Bearer ${token}`;

  // The caller's signal and the idle timer both feed one internal controller,
  // so the existing abort plumbing keeps working and the timeout composes with
  // it rather than replacing it.
  const controller = new AbortController();
  const onCallerAbort = () => controller.abort(signal?.reason);
  if (signal?.aborted) controller.abort(signal.reason);
  else signal?.addEventListener("abort", onCallerAbort);

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let idleTimer;
  let timedOut = false;
  const armIdleTimer = () => {
    if (!(idleTimeoutMs > 0)) return;
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, idleTimeoutMs);
  };

  try {
    armIdleTimer();

    const res = await fetch(endpoint, {
      method: "POST",
      credentials: "include", // for Supabase cookie auth
      headers,
      body: JSON.stringify({ image_b64: imageB64, prompt }),
      signal: controller.signal,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`${endpoint} failed: ${res.status} ${text.slice(0, 200)}`);
    }
    if (!res.body) {
      throw new Error(`${endpoint}: no response body`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        armIdleTimer();
        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by blank lines (\n\n).
        let sep;
        while ((sep = buffer.indexOf("\n\n")) >= 0) {
          const chunk = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          const line = chunk.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const json = line.slice("data:".length).trim();
          if (json.length === 0) continue;
          try {
            yield /** @type {InferenceEvent} */ (JSON.parse(json));
          } catch (e) {
            // Malformed event payload; surface as an error event so callers
            // don't silently lose info.
            yield {
              event: "error",
              reason: "client-parse-error",
              message: `failed to parse SSE payload: ${e.message}`,
            };
          }
        }
      }
    } finally {
      // A consumer that breaks out of the `for await` early lands here with the
      // body still open; releasing the lock alone leaves the connection held
      // until GC, so cancel the body first.
      await reader.cancel().catch(() => {});
      reader.releaseLock();
    }
  } catch (e) {
    if (timedOut) {
      throw new Error(`${endpoint}: no data for ${idleTimeoutMs}ms, giving up`);
    }
    throw e;
  } finally {
    clearTimeout(idleTimer);
    signal?.removeEventListener("abort", onCallerAbort);
  }
}

/** @typedef {Object} InferenceEvent
 *  @property {"warming"|"running"|"complete"|"error"} event
 *  @property {number} [eta_s]
 *  @property {*} [payload]
 *  @property {string} [reason]
 *  @property {string} [message]
 */
